import { useEffect, useMemo, useState } from 'react';
import { differenceBy } from 'lodash';

import { useFitment, useGraphQLClient, useMyStore, useQuery } from '../hooks';

import {
  GET_BRAND_CATEGORIES_BY_SEO_NAME,
  GET_CATEGORY_BY_SEO_NAME,
  GET_CATEGORY_PAGE,
  GET_PRODUCTS_BY_BRAND_AND_CATEGORIES,
  GET_TAXONOMY_CATEGORIES
} from '../../../shared/graphql/category-queries';

import { getVehicleInput } from '../../utils/fitment/fitment';
import { isArrayWithLength } from '../../utils/validators/validators';

export default function useCategories({
  groupProductsByCategory = false,
  includeNoFitCategories = false,
  includeProducts = false,
  isTaxonomy = false,
  productType = undefined,
  seoName
}) {
  const { myStore } = useMyStore();
  const { selectedFitment } = useFitment();
  const [products, setProducts] = useState();
  const [isProductsLoading, setIsProductsLoading] = useState();
  const client = useGraphQLClient();

  const { data: categoryBySeoData = {}, isLoading: isCategoryBySeoLoading } =
    useQuery(GET_CATEGORY_BY_SEO_NAME, {
      skip: !seoName,
      variables: { seoName }
    });

  const vehicleInfo = useMemo(
    () => getVehicleInput(selectedFitment),
    [selectedFitment]
  );

  const {
    data: brandCategoriesByCodeThatFitData = {},
    isLoading: isBrandCategoriesByCodeThatFitLoading
  } = useQuery(GET_BRAND_CATEGORIES_BY_SEO_NAME, {
    skip: isTaxonomy || !seoName || !myStore?.code,
    variables: {
      onlyThatFits: Boolean(vehicleInfo),
      productType,
      seoName,
      storeCode: myStore?.code,
      vehicleInfo
    }
  });

  const brandCategoriesThatFit =
    brandCategoriesByCodeThatFitData?.brandCategory?.bySeoName;
  const shouldFetchAllBrandCategories =
    includeNoFitCategories ||
    (brandCategoriesByCodeThatFitData?.brandCategory &&
      !isArrayWithLength(brandCategoriesThatFit));

  const {
    data: allBrandCategoriesByCodeData = {},
    isLoading: isBrandCategoriesByCodeLoading
  } = useQuery(GET_BRAND_CATEGORIES_BY_SEO_NAME, {
    skip:
      isTaxonomy ||
      !seoName ||
      !myStore?.code ||
      !shouldFetchAllBrandCategories,
    variables: {
      onlyThatFits: false,
      productType,
      seoName,
      storeCode: myStore?.code,
      vehicleInfo
    }
  });

  const { data: taxonomyFitData, isLoading: isTaxonomyFitLoading } = useQuery(
    GET_TAXONOMY_CATEGORIES,
    {
      skip: !isTaxonomy || !myStore?.code || !seoName,
      variables: {
        onlyThatFits: Boolean(vehicleInfo),
        storeCode: myStore?.code,
        taxonomySeoName: seoName,
        vehicleInfo
      }
    }
  );

  const taxonomyCategoriesThatFit =
    taxonomyFitData?.taxonomyCategory?.bySeoName;
  const shouldFetchAlltaxonomyCategories =
    includeNoFitCategories ||
    (taxonomyFitData?.taxonomyCategory &&
      !isArrayWithLength(taxonomyCategoriesThatFit));

  const {
    data: allTaxonomyCategoriesByCodeData,
    isLoading: isAllTaxonomyLoading
  } = useQuery(GET_TAXONOMY_CATEGORIES, {
    skip:
      !isTaxonomy ||
      !myStore?.code ||
      !seoName ||
      !shouldFetchAlltaxonomyCategories,
    variables: {
      onlyThatFits: false,
      storeCode: myStore?.code,
      taxonomySeoName: seoName,
      vehicleInfo
    }
  });

  const { data: cmsData, isLoading: isCmsLoading } = useQuery(
    GET_CATEGORY_PAGE,
    {
      skip: !seoName,
      variables: {
        id: seoName
      }
    }
  );

  const { cms } = cmsData ?? {};

  const allBrandCategories =
    allBrandCategoriesByCodeData?.brandCategory?.bySeoName;

  const allTaxonomyCategories =
    allTaxonomyCategoriesByCodeData?.taxonomyCategory?.bySeoName;

  let categoryData;
  let doesFitCategoryData;
  let allTaxonomyData;
  let noFitCategoryData;

  if (isTaxonomy) {
    categoryData = taxonomyCategoriesThatFit;

    if (includeNoFitCategories) {
      allTaxonomyData = allTaxonomyCategories ?? [];
      noFitCategoryData = differenceBy(allTaxonomyData, categoryData, 'code');
    }
  } else if (includeNoFitCategories) {
    const allCategoryData = allBrandCategories ?? [];
    doesFitCategoryData = brandCategoriesThatFit ?? [];
    noFitCategoryData = differenceBy(
      allCategoryData,
      doesFitCategoryData,
      'code'
    );
  } else {
    categoryData = isArrayWithLength(brandCategoriesThatFit)
      ? brandCategoriesThatFit
      : allBrandCategories;
  }

  const shouldFetchProducts =
    includeProducts &&
    !isBrandCategoriesByCodeLoading &&
    !isBrandCategoriesByCodeThatFitLoading &&
    (isArrayWithLength(allBrandCategories) ||
      isArrayWithLength(brandCategoriesThatFit));

  useEffect(() => {
    if (!shouldFetchProducts) {
      return;
    }

    setIsProductsLoading(true);

    client
      .query({
        query: GET_PRODUCTS_BY_BRAND_AND_CATEGORIES,
        variables: {
          categorySeoName: seoName,
          storeCode: myStore?.code,
          vehicleInfo: isArrayWithLength(brandCategoriesThatFit)
            ? vehicleInfo
            : undefined
        }
      })
      .then(result => {
        setIsProductsLoading(false);
        setProducts(
          groupProductsByCategory
            ? result?.data?.productSearch?.byCategory?.results?.reduce?.(
                (acc, product) => {
                  //group products by category
                  product?.line?.forEach(l => {
                    acc[l] = (acc[l] ?? []).concat([product]);
                  });

                  return acc;
                },
                {}
              )
            : result?.data?.productSearch?.byCategory?.results
        );
      })
      .catch(() => {
        // ignore failed product requests
        setIsProductsLoading(false);
      });
  }, [
    brandCategoriesThatFit,
    groupProductsByCategory,
    shouldFetchProducts,
    client,
    myStore,
    productType,
    seoName,
    vehicleInfo
  ]);

  useEffect(() => {
    setProducts(null);
  }, [seoName]);

  return {
    category: !isCategoryBySeoLoading
      ? categoryBySeoData?.categoryBySeoName
      : null,
    categoryData: doesFitCategoryData ?? categoryData,
    cms,
    isLoading:
      isBrandCategoriesByCodeLoading ||
      isBrandCategoriesByCodeThatFitLoading ||
      isCategoryBySeoLoading ||
      isTaxonomyFitLoading ||
      isAllTaxonomyLoading ||
      isCmsLoading,
    isProductsLoading,
    noFitCategoryData,
    products: !isProductsLoading && includeProducts ? products : null
  };
}
